import { useEffect, useMemo, useState } from 'react';
import { Api, type User } from './core/api';
import { platform } from './core/platform';
import { BrowserView } from './ui/BrowserView';
import { DirectBrowser } from './ui/DirectBrowser';
import { LoginPage } from './ui/LoginPage';

type Mode = 'direct' | 'cloud';
const MODE_KEY = 'gcb.mode';

function initialMode(): Mode {
  try {
    const m = localStorage.getItem(MODE_KEY);
    if (m === 'direct' || m === 'cloud') return m;
  } catch {
    /* storage unavailable */
  }
  // Direct mode needs service workers on a second origin – the iOS app (WKWebView)
  // can't do that reliably, so it starts in cloud mode.
  return platform().name === 'web' ? 'direct' : 'cloud';
}

export function App() {
  const api = useMemo(() => new Api(), []);
  const [user, setUser] = useState<User | null | undefined>(undefined);
  const [mode, setModeState] = useState<Mode>(initialMode);
  const [cloudUrl, setCloudUrl] = useState<string>();

  const setMode = (m: Mode) => {
    setModeState(m);
    try {
      localStorage.setItem(MODE_KEY, m);
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    if (platform().needsServerUrl && !api.base) {
      setUser(null);
      return;
    }
    // Only a real 401 means "signed out". Network errors / 5xx (server restarting)
    // are retried instead of dropping the user on the login page.
    let stop = false;
    let timer: ReturnType<typeof setTimeout>;
    const check = (attempt: number) =>
      api
        .me()
        .then((u) => !stop && setUser(u))
        .catch(() => {
          if (stop) return;
          if (attempt >= 8) setUser(null);
          else timer = setTimeout(() => check(attempt + 1), Math.min(4000, 500 * 2 ** attempt));
        });
    check(0);
    return () => {
      stop = true;
      clearTimeout(timer);
    };
  }, [api]);

  if (user === undefined) {
    return (
      <div className="splash">
        <span className="spinner" />
      </div>
    );
  }
  if (!user) return <LoginPage api={api} onLogin={setUser} />;

  const logout = async () => {
    await api.logout();
    setUser(null);
  };

  if (mode === 'direct') {
    return (
      <DirectBrowser
        api={api}
        onLogout={logout}
        onCloudMode={(url) => {
          setCloudUrl(url);
          setMode('cloud');
        }}
      />
    );
  }
  return <BrowserView api={api} onLogout={logout} initialUrl={cloudUrl} onDirectMode={() => setMode('direct')} />;
}
